import { Component, DetailEvent, Entity, registerComponent, registerSystem, System } from "aframe";
import { Quaternion, Vector3, Vector3Like } from "three";
import { LocationSystem } from "../location";
import { RTCClient, myself } from "../../rtc/client";
import { Guid, parseGuid } from "../../rtc/guid";
import { MessageData, ProtocolMessage } from "../../rtc/protocol";
import { AmmoBodyComponent } from "../ammo";
import { SlotEntity, SlotEvent } from "../slot";
import { RtcEntity, RtcEntityCreationOptions, RtcEntitySystem } from "./rtc-entity";

interface SpawnEventDetail
{
	template : string | Guid;
	variables : any;
	transform? : Entity; //If present, spawned entity should not be attached, but its transform sj
}

export interface RtcChannelComponent extends Component
{
	el : RtcChannelEntity;
	data : {
		id : Guid; //The actual instance ID
	};
	joined : boolean;
	leave() : void;
}
interface RtcChannelComponentInternal extends RtcChannelComponent
{
	properties : CreatePayload;
	spawnListener : EventListener;
	leaveListener : EventListener;
	system : RtcChannelSystemInternal;
	processMessage(code : number, emitter : Guid, time : number, payload : Uint8Array) : void;
}
export type RtcChannelEntity = Entity< { [attrName:string] : Component } & { "rtc-channel" : RtcChannelComponent } > & 
{
	rtc : RtcChannelComponent;
};

export interface RtcChannelSystem extends System
{
	getChannel( id : Guid ) : RtcChannelEntity;
}

interface RtcChannelSystemInternal extends RtcChannelSystem
{
	data : {
		light? : Entity;
	};

	//joined : RtcChannelComponentInternal[];

	channels : { [id : string ] : RtcChannelEntity };
	client? : RTCClient;
	protocol : ControlCodes;

	processMessage(data : MessageData) : void;
	updateWorld(joined? : RtcChannelComponent, left? : RtcChannelComponent) : void;
	updateAudio(joined? : RtcChannelComponent, left? : RtcChannelComponent) : void;
	updateChat(joined? : RtcChannelComponent, left? : RtcChannelComponent) : void;


}


interface ControlCodes
{
	Create : ProtocolMessage;
	Join : ProtocolMessage;
	Leave : ProtocolMessage;
	PostChatMessage : ProtocolMessage;
	Destroy : ProtocolMessage;
}




interface WorldChannelSystem extends System
{
	data : {
		light? : Entity;
		ambientLight : string;
		directLight : string;
		sky? : Entity;
		skyMaterial : string;
	};
	update() : void;
}

interface WorldChannelComponent extends Component
{
	data : {
		ambientLight : number;
		directLight : Vector3Like;
		skyMap : string;
		skyColor : string;
	};
	system : WorldChannelSystem;
}


type WorldChannelEntity = Entity< { [attrName:string] : Component } & { "world-channel" : WorldChannelComponent } >;

registerSystem("world-channel", {
	schema : {
		light : { type : "selector", default : "a-scene a-entity[light__ambient][light__direct]" },
		ambientLight : { type: "string", default : "light__ambient" },
		directLight : { type: "string", default : "light__direct" },
		sky : { type : "selector", default : "a-scene a-entity[material__sky]" },
		skyMaterial : { type:  "string", default: "material__sky" }
	},

	init(this : WorldChannelSystem)
	{
		const updateListener = (event : Event) =>
		{
			const el = ( event as DetailEvent<Component> ).detail.el; //event.target as Entity;
			if(el.components["world-channel"])
				this.update();
		};
		this.el.addEventListener("channel-joined", updateListener);
		this.el.addEventListener("channel-left", updateListener);
	},

	update(this : WorldChannelSystem)
	{
		const channels = Array.from(this.el.querySelectorAll<WorldChannelEntity>(":scope [world-channel].joined"));

		const lightEl = this.data.light, skyEl = this.data.sky;
		if(lightEl)
		{
			let ambient = 0.0, direct = new Vector3(), count = 0;
			for(let ch of channels)
			{
				const data = ch.components["world-channel"]?.data;
				if(data && data.ambientLight > ambient)
					ambient = data.ambientLight;
				if(data && data.directLight)
				{
					direct.add(data.directLight);
					++count;
				}
			}

			const dl = direct.length();
			if(dl > 0)
			{
				direct.multiplyScalar(1.0 / dl); //Normalize
				lightEl.setAttribute(this.data.ambientLight, { intensity : ambient });
				lightEl.setAttribute(this.data.directLight, { intensity : dl / count });
				lightEl.setAttribute("position", direct);
			}
		}

		if(skyEl)
		{
			for(let ch of channels)
			{
				const data = ch.components["world-channel"]?.data;
				if(data && data.skyMap)
				{
					skyEl.setAttribute(this.data.skyMaterial, { src : data.skyMap, color : data.skyColor } );
					break;
				}
			}

		}

	}
});


registerComponent("world-channel", {
	schema : {
		ambientLight : { type : "number", default : 0.7 },
		directLight : {type : "vec3", default : {x:0.1, y:0.7, z:0.2} },
		skyMap : { type : "string" },
		skyColor : { type : "color", default : "#FFF" }
	},
	update(this : WorldChannelComponent) { this.system.update(); },
	remove(this : WorldChannelComponent) { this.system.update(); }
});

registerComponent("audio-channel", {
	schema : {
		attenuation : { type : "number", default : 0.7 },
		spatialization : { type : "number", default : 0.7 }
	}
});





registerSystem("rtc-channel", {


	schema : {
		light : { type : "selector", default : "a-scene a-entity[light__ambient][light__direct]" }
	},


	init(this : RtcChannelSystemInternal)
	{
		this.channels = {};
		//this.joined = [];

		const joinListener = (event : DetailEvent<Guid>) =>
		{
			if(!this.client) return;

			const body : JoinPayload = { prototypeId : event.detail };
			this.client.sendObjectMessage(this.protocol!.Join, event.detail, body);
			event.preventDefault();
		};
		
		this.el.addEventListener("rtc-connected", (event) =>
		{
			this.client = (event as DetailEvent<RTCClient>).detail;

			this.el.addEventListener("join-channel", joinListener as EventListener);

			const controlCodes : { [name:string] : ProtocolMessage } = {};
			for(let pm of this.client.protocol!.channel)
				controlCodes[pm.name] = pm;
			this.protocol = controlCodes as any;

			const firstCode = controlCodes.Create!.code, lastCode = controlCodes.Destroy!.code;

			const onMessage = (event : DetailEvent<MessageData>) => 
				(event.detail.code >= firstCode) && (event.detail.code <= lastCode) &&
				this.processMessage(event.detail);
			
			this.client.addEventListener("message", onMessage as EventListener);
		});
	},

	getChannel(this : RtcChannelSystemInternal, id : Guid)
	{
		return this.channels[ id.toString() ];

	},

	



	processMessage(this : RtcChannelSystemInternal, message : MessageData)
	{
		//const reader = new Reader(message.data);
		const channelGuid = new Guid(message.data.subarray(0, 16));
		const channelId = channelGuid.toString();

		let channel = this.channels[channelId];
		if(!channel)
		{
			if(message.code == this.protocol.Leave.code)
			{
				return;
			}

			channel = this.el.ownerDocument.createElement("a-entity") as RtcChannelEntity;
			channel.setAttribute("rtc-channel", { id : channelGuid });
			channel.id = channelGuid.toString();
			this.channels[channelId] = channel;
			this.el.append(channel);
		}

		if( (message.code == this.protocol.Leave.code) && message.emitter.equals(myself) )
		{
			delete this.channels[channelId];
			if(channel)
			{
				channel.removeAttribute("id");
				channel.classList.remove("joined");
				//channel.remove();
			}
		}

		if(channel.hasLoaded)
		{
			const component = channel.components["rtc-channel"] as RtcChannelComponentInternal;
			return component.processMessage(message.code, message.emitter, message.time, message.data.subarray(16));
		}

		
		channel.addEventListener("loaded", () =>
		{
			const component = channel.components["rtc-channel"] as RtcChannelComponentInternal;
			component.processMessage(message.code, message.emitter, message.time, message.data.subarray(16));
		}, { once : true });
	}
});



interface CreatePayload
{
	flags : number;
	world ? : { 
		ambientLight : number,
		directLight : Vector3Like
	};
	audio ? : {
		attenuation : number,
		spatialization : number
	};
	chat ? : {
	}
};
interface JoinPayload
{
};
interface PostChatMessagePayload
{
	text : string;
	imageIds : Guid[];
};



registerComponent("rtc-channel", {
	schema : { 
		id : {
			default: Guid.empty,
			parse: parseGuid,
			stringify: (v : any) => v && v.toString()
		}
	},
	init(this : RtcChannelComponentInternal)
	{
		this.el.rtc = this;
		
		this.spawnListener = (event : Event) =>
		{
			const detail = (event as DetailEvent<SpawnEventDetail>).detail;

			const templateId = (detail.template instanceof Guid) ? detail.template : parseGuid(detail.template);
			if(!templateId)
				return console.warn("Missing/Invalid template Id", detail.template);

			const entitySystem = this.system.el.systems["rtc-entity"] as RtcEntitySystem;

			const attachEntity = (entity : RtcEntity) => {

				//Entity is created in channel but not attached to anything, thus not visible to anyone.
				const slotEl = event.target as SlotEntity;
				
				//Count starts at 1 because we've waited for entity.ready
				let templateCount = 1;

				const templateLoading = () => ++templateCount;
				const templateLoaded = () => {
					entity.emit("attach-to-slot", slotEl); // try again
					--templateCount;
					if(templateCount > 0) return; // Still a chance...
					if(entity.object3D.visible) return; // Yay!

					//This was our last chance : put it wherever we can
					if(slotEl && slotEl.object3D)
					{
						slotEl.object3D.updateWorldMatrix(true, false);
						slotEl.object3D.matrixWorld.decompose( entity.object3D.position, entity.object3D.quaternion, {} as any );
						entity.object3D.updateWorldMatrix(true, true);

						const ammoBody = entity.components["ammo-body"] as AmmoBodyComponent;
						ammoBody && ammoBody.moveTo(entity.object3D.position, entity.object3D.quaternion);
					}
					entity.emit("aligned-to-slot", {slot:slotEl}, false );
				};

				
				
				const attachedListener = ((event : SlotEvent) =>
				{
					if(event.detail.slot !== slotEl) return;
					entity.removeEventListener("attached-to-slot", attachedListener);
					entity.removeEventListener("aligned-to-slot", attachedListener);
					entity.removeEventListener("template-loaded", templateLoaded);
					entity.removeEventListener("template-loading", templateLoading);
					if(event.type == "aligned-to-slot")
					{
						const rtcEntity = entity.components["rtc-entity"];
						rtcEntity.sendTransform();
						rtcEntity.attachTo(null);
					}
					entity.object3D.visible = true;
					if(entity.components["ammo-body"]) // Enable ammo-body, because we won't receive the Attach message (we just sent it)
						entity.setAttribute("ammo-body", { enabled : true } );
				}) as EventListener;
				entity.addEventListener("attached-to-slot", attachedListener);
				entity.addEventListener("aligned-to-slot", attachedListener);
				entity.addEventListener("template-loaded", templateLoaded);
				entity.addEventListener("template-loading", templateLoading);

				templateLoaded();
			};


			const options : RtcEntityCreationOptions = { };

			if(detail.transform)
			{
				const pos = new Vector3(), rot = new Quaternion();
				detail.transform.object3D.matrixWorld.decompose(pos, rot, {} as any );
				options.transform = { position : pos, rotation : rot };
				options.attach = { }; //Attach to nothing.
			}

			if(detail.variables)
			{
				options.variables = {};
				for(let key in detail.variables)
					options.variables[key] = detail.variables[key];
			}

			entitySystem.createEntityAsync(templateId!, this.el, options).then( 
				(entity) => { if(!options.transform) entity.ready.then( () => attachEntity(entity) ); },
				(reason) => console.warn(reason) 
			);

		};

		this.leaveListener = (event : Event) => this.leave();
		
		this.el.addEventListener("spawn-entity", this.spawnListener);
		this.el.addEventListener("leave-channel", this.leaveListener);
	},
	remove(this : RtcChannelComponentInternal)
	{
		this.el.removeAttribute("world-channel");
		this.el.removeAttribute("audio-channel");
		this.el.removeEventListener("spawn-entity", this.spawnListener);
		this.el.removeEventListener("leave-channel", this.leaveListener);
	},
	leave(this : RtcChannelComponentInternal)
	{
		if(!this.joined) return; //Already left

		this.joined = false;

		(this.el.sceneEl!.systems.location as LocationSystem).removeChannel( 'rtc:' + String(this.data.id) );

		const client = this.system.client;
		client && client.sendObjectMessage( this.system.protocol!.Leave, this.data.id, {} );
	},
	processMessage(this:RtcChannelComponentInternal, code : number, emitter : Guid, time : number, payload : Uint8Array)
	{
		const protocol = this.system.protocol;
		const client = this.system.client!;

		if(code == protocol.Create.code) //Channel was created
		{
			const context = 
			{
				//TODO : move condition logic into Protocol
				"HasWorld" : (obj : { flags : number }) => (obj.flags & 1),
				"HasAudio" : (obj : { flags : number }) => (obj.flags & 2),
				"HasChat" : (obj : { flags : number }) => (obj.flags & 4)
			};
			const data = client.deserialize(protocol.Create, payload, context) as CreatePayload;
			this.properties = data;

			if(data.world)
				this.el.setAttribute("world-channel", data.world);
			if(data.audio)
				this.el.setAttribute("audio-channel", data.audio);

			this.el.emit("rtc-channel-created", data);
		}
		else if(code == protocol.Join.code) //Emitter avatar joined
		{
			const data = client.deserialize(protocol.Join, payload) as JoinPayload;

			//console.dir( { join : data });

			if(emitter.equals(myself) && !this.joined)
			{
				this.joined = true;
				this.el.classList.add("joined"); //Add a joined class to facilitate "the channel I'm in" query selector
				this.el.emit("channel-joined", this);
				//this.system.joined.push(this);
			}
		}
		else if(code == protocol.Leave.code) //Emitter avatar left
		{
			if(emitter.equals(myself) /* && this.joined */)
			{
				this.joined = false;
				this.el.classList.remove("joined");
				this.el.emit("channel-left", this);
				/* for(let ix = this.system.joined.length; --ix >= 0; )
					if(this.system.joined[ix] === this)
					{
						this.system.joined.splice(ix, 1);
					} */

				this.el.remove(); //Remove channel and all its owned entities, before we're told to forget them.
			}
		}
		else if(code == protocol.PostChatMessage.code) //Emitter avatar sent a message
		{
			const data = client.deserialize(protocol.PostChatMessage, payload) as PostChatMessagePayload;
			
		}
		else if(code == protocol.Destroy.code) //Channel was destroyed
		{

		}
	}
});





